import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from "react";

interface Toast {
  id: string;
  type: "success" | "error" | "info" | "warning";
  title?: string;
  message: string;
}

interface ToastOptions {
  title?: string;
  description?: string;
  type?: "success" | "error" | "info" | "warning";
  variant?: "success" | "error" | "info" | "warning";
  message?: string;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (typeOrOptions: Toast["type"] | ToastOptions, message?: string) => void;
  toast: (options: ToastOptions) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
    };
  }, []);

  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToastInternal = useCallback((type: Toast["type"], title: string, message?: string) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, type, title, message: message || title }]);
    const timer = setTimeout(() => {
      timersRef.current.delete(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
    timersRef.current.set(id, timer);
  }, []);

  const addToast = useCallback(
    (typeOrOptions: Toast["type"] | ToastOptions, message?: string) => {
      if (typeof typeOrOptions === "string") {
        addToastInternal(typeOrOptions, message || "");
      } else {
        const opts = typeOrOptions;
        const type = opts.variant || opts.type || "info";
        const title = opts.title || "";
        const msg = opts.description || opts.message || opts.title || "";
        addToastInternal(type, title, msg);
      }
    },
    [addToastInternal]
  );

  const toast = useCallback(
    (options: ToastOptions) => {
      const type = options.variant || options.type || "info";
      const title = options.title || "";
      const msg = options.description || options.message || options.title || "";
      addToastInternal(type, title, msg);
    },
    [addToastInternal]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, toast, removeToast }}>
      {children}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 max-w-sm">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`ds-card px-4 py-3 text-sm animate-slide-up cursor-pointer ${
              t.type === "success"
                ? "border-ds-success/30 text-ds-success"
                : t.type === "error"
                ? "border-ds-danger/30 text-ds-danger"
                : t.type === "warning"
                ? "border-ds-warning/30 text-ds-warning"
                : "border-ds-accent/30 text-ds-accent"
            }`}
            onClick={() => removeToast(t.id)}
          >
            {t.title && t.title !== t.message && (
              <div className="mb-0.5 font-medium text-ds-text">{t.title}</div>
            )}
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
